import React, { useState, useEffect } from 'react';
import { eventAPI, utilsAPI } from '../../../api';
import EventForm from './EventForm';
import './EventList.css';
import { Link } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';

const EventList = () => {
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Récupérer l'URL de base pour les IMAGES
    const getImageBaseUrl = () => {
        const apiUrl = process.env.REACT_APP_API_URL;
        
        console.log('Event List Admin - Image Base URL Detection:', {
            apiUrl: apiUrl,
            origin: window.location.origin,
            nodeEnv: process.env.NODE_ENV
        });
        
        if (apiUrl) {
            // Retirer "/api" de l'URL si présent
            return apiUrl.replace('/api', '');
        } else if (window.location.origin.includes('localhost') || window.location.origin.includes('127.0.0.1')) {
            return 'http://127.0.0.1:8000';
        } else {
            return window.location.origin;
        }
    };

    // Fonction pour construire l'URL de l'image - CORRIGÉE POUR UTILISER L'API
    const getImageUrl = (event) => {
        if (!event) return getFallbackImage();
        
        console.log('Processing event image for admin list:', {
            id: event.id,
            title: event.title,
            hasImage: !!event.image,
            hasImageUrl: !!event.image_url,
            image: event.image,
            image_url: event.image_url
        });
        
        // 1. Priorité : utiliser image_url de l'API si disponible
        if (event.image_url) {
            console.log('Using API image_url:', event.image_url);
            
            // Si c'est déjà une URL complète
            if (event.image_url.startsWith('http://') || event.image_url.startsWith('https://')) {
                return event.image_url;
            }
            
            // Si c'est un chemin relatif, ajouter le domaine
            const baseUrl = getImageBaseUrl();
            if (event.image_url.startsWith('/')) {
                return `${baseUrl}${event.image_url}`;
            }
            
            // Sinon, retourner tel quel
            return event.image_url;
        }
        
        // 2. Sinon, utiliser event.image
        if (!event.image) {
            console.log('No image path provided for event');
            return getFallbackImage(event.title);
        }
        
        console.log('Processing event image path:', event.image);
        
        // Si c'est déjà une URL complète
        if (event.image.startsWith('http://') || event.image.startsWith('https://')) {
            console.log('Full URL detected:', event.image);
            return event.image;
        }
        
        // Construire l'URL via l'API (comme pour les blogs et annonces)
        const baseUrl = getImageBaseUrl();
        const fileName = event.image.split('/').pop();
        
        // Utiliser le pattern API
        const apiImageUrl = `${baseUrl}/api/storage/events/${fileName}`;
        
        console.log('Built Event Image URL (via API):', {
            original: event.image,
            fileName: fileName,
            apiUrl: apiImageUrl
        });
        
        return apiImageUrl;
    };

    // Fonction pour le fallback d'image SVG local
    const getFallbackImage = (eventTitle = 'Événement ETEF') => {
        return `data:image/svg+xml;utf8,${encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="100" height="60" viewBox="0 0 100 60">
                <rect width="100" height="60" fill="#f8f9fa"/>
                <text x="50" y="30" font-family="Arial, sans-serif" font-size="8" text-anchor="middle" fill="#6c757d" dy=".3em">
                    Événement
                </text>
                <text x="50" y="45" font-family="Arial, sans-serif" font-size="6" text-anchor="middle" fill="#adb5bd" dy=".3em">
                    ${eventTitle.substring(0, 20)}
                </text>
            </svg>
        `)}`;
    };

    const fetchEvents = async (page = 1) => {
        try {
            setLoading(true);
            const response = await eventAPI.getMyEvents(page);
            
            console.log('Events API Response (admin):', response);
            
            if (response.success && response.data) {
                // Ajouter l'URL d'image à chaque événement
                const eventsWithImageUrl = (response.data.data || []).map(event => ({
                    ...event,
                    image_url: event.image ? getImageUrl(event) : getFallbackImage(event.title)
                }));
                
                console.log('Events with image URLs:', eventsWithImageUrl.map(e => ({
                    id: e.id,
                    title: e.title,
                    image: e.image,
                    image_url: e.image_url
                })));
                
                setEvents(eventsWithImageUrl);
                setTotalPages(response.data.last_page || 1);
                setCurrentPage(response.data.current_page || 1);
            }
        } catch (err) {
            console.error('Error fetching events:', err);
            setError(err.message || 'Erreur lors de la récupération des événements');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEvents();
    }, []);

    // Gestionnaire d'erreur d'image avec fallback
    const handleImageError = (e, event) => {
        console.error('Failed to load event image in admin:', {
            eventId: event?.id,
            eventTitle: event?.title,
            attemptedUrl: e.target.src,
            original: event?.image,
            timestamp: new Date().toISOString()
        });
        
        // Essayer l'URL directe sans /api/ comme fallback
        if (event?.image) {
            const baseUrl = getImageBaseUrl();
            const fileName = event.image.split('/').pop();
            const directUrl = `${baseUrl}/storage/events/${fileName}`;
            
            console.log('Trying direct storage URL as fallback (admin):', directUrl);
            e.target.src = directUrl;
        } else {
            // Fallback final
            e.target.src = getFallbackImage(event?.title);
            e.target.style.opacity = '0.8';
            e.target.style.filter = 'grayscale(20%)';
        }
    };

    const handleAddEvent = () => {
        setEditingEvent(null);
        setShowForm(true);
    };

    const handleEditEvent = (event) => {
        setEditingEvent(event);
        setShowForm(true);
    };

    const handleDeleteEvent = async (id) => {
        if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ?')) {
            return;
        }

        try {
            const response = await eventAPI.delete(id);
            if (response.success) {
                fetchEvents(currentPage);
                alert('Événement supprimé avec succès');
            }
        } catch (err) {
            console.error('Error deleting event:', err);
            alert(err.message || 'Erreur lors de la suppression');
        }
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingEvent(null);
    };

    const handleFormSubmit = () => {
        setShowForm(false);
        setEditingEvent(null);
        fetchEvents(currentPage);
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handlePageChange = (page) => {
        if (page >= 1 && page <= totalPages) {
            fetchEvents(page);
        }
    };

    const toggleEventStatus = async (event) => {
        try {
            const updatedEvent = { ...event, is_active: !event.is_active };
            const response = await eventAPI.update(event.id, updatedEvent);
            
            if (response.success) {
                fetchEvents(currentPage);
            }
        } catch (err) {
            console.error('Error updating event status:', err);
            alert(err.message || 'Erreur lors de la mise à jour');
        }
    };

    if (loading && !events.length) {
        return (
            <div className="loading-container">
                <div className="spinner"></div>
                <p>Chargement des événements...</p>
            </div>
        );
    }

    return (
        <div className="event-admin-container">
            <Link 
                to="/admin" 
                className="btn-back-admin"
                title="Retour à l'administration"
            >   
                <FaArrowLeft className="back-arrow-icon" />
            </Link>
            <div className="event-header">
                <h2>Gestion des événements</h2>
                <button onClick={handleAddEvent} className="btn-add-event">
                    <i className="fas fa-plus"></i> Ajouter un événement
                </button>
            </div>

            {error && (
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={() => fetchEvents()} className="btn-retry">
                        Réessayer
                    </button>
                </div>
            )}

            {showForm && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <EventForm 
                            event={editingEvent}
                            onClose={handleCloseForm}
                            onSubmit={handleFormSubmit}
                        />
                    </div>
                </div>
            )}

            {events.length === 0 ? (
                <div className="no-events">
                    <i className="fas fa-calendar-times"></i>
                    <p>Aucun événement créé</p>
                    <button onClick={handleAddEvent} className="btn-add-first">
                        Créer votre premier événement
                    </button>
                </div>
            ) : (
                <>
                    <div className="events-table-container">
                        <table className="events-table">
                            <thead>
                                <tr>
                                    <th>Titre</th>
                                    <th>Date de début</th>
                                    <th>Date de fin</th>
                                    <th>Lieu</th>
                                    <th>Participants</th>
                                    <th>Prix</th>
                                    <th>Statut</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {events.map((event) => {
                                    const imageUrl = event.image_url || getImageUrl(event);
                                    
                                    return (
                                        <tr key={event.id}>
                                            <td>
                                                <div className="event-title-cell">
                                                    <img 
                                                        src={imageUrl}
                                                        alt={event.title}
                                                        className="event-thumbnail"
                                                        onError={(e) => handleImageError(e, event)}
                                                        onLoad={(e) => {
                                                            console.log('✅ Event image loaded successfully in admin for event:', {
                                                                eventId: event.id,
                                                                eventTitle: event.title,
                                                                imageUrl: e.target.src
                                                            });
                                                        }}
                                                    />
                                                    <div>
                                                        <strong>{event.title}</strong>
                                                        <small>{event.description ? `${event.description.substring(0, 50)}...` : 'Pas de description'}</small>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{formatDate(event.start_date)}</td>
                                            <td>{formatDate(event.end_date)}</td>
                                            <td>{event.location}</td>
                                            <td>
                                                {event.max_participants 
                                                    ? `${event.current_participants || 0}/${event.max_participants}`
                                                    : 'Illimité'
                                                }
                                            </td>
                                            <td>
                                                {event.price > 0 
                                                    ? `${event.price} €`
                                                    : 'Gratuit'
                                                }
                                            </td>
                                            <td>
                                                <button 
                                                    onClick={() => toggleEventStatus(event)}
                                                    className={`status-btn ${event.is_active ? 'active' : 'inactive'}`}
                                                >
                                                    {event.is_active ? 'Actif' : 'Inactif'}
                                                </button>
                                            </td>
                                            <td>
                                                <div className="action-buttons">
                                                    <button 
                                                        onClick={() => handleEditEvent(event)}
                                                        className="btn-edit"
                                                        title="Modifier"
                                                    >
                                                        <i className="fas fa-edit"></i>
                                                    </button>
                                                    <button 
                                                        onClick={() => handleDeleteEvent(event.id)}
                                                        className="btn-delete"
                                                        title="Supprimer"
                                                    >
                                                        <i className="fas fa-trash"></i>
                                                    </button>
                                                    <a 
                                                        href={`/events/${event.slug || event.id}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="btn-view"
                                                        title="Voir"
                                                    >
                                                        <i className="fas fa-eye"></i>
                                                    </a>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {totalPages > 1 && (
                        <div className="pagination">
                            <button 
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="page-btn"
                            >
                                <i className="fas fa-chevron-left"></i>
                            </button>
                            
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }
                                
                                return (
                                    <button
                                        key={pageNum}
                                        onClick={() => handlePageChange(pageNum)}
                                        className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                            
                            <button 
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="page-btn"
                            >
                                <i className="fas fa-chevron-right"></i>
                            </button>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default EventList;